import {STORIES} from '../dummy_data/sampleStories';
import {DIAGNOSTIC_DATA} from '../dummy_data/reports/sample_diagnostic_report';
import Amplify, { API, Analytics, Storage, graphqlOperation, Auth } from 'aws-amplify';
import {poll} from '../library';
import studentRecordService from './StudentRecordService';
import studentInformationService from './StudentInformationService';
import {getRegionalURL, searchSessionAudio} from './SpeechStream';
import {
  userTypes,
  assignmentTypes,
  activityStatus,
  recommendationModes,
  locales,
  phraseTypes
} from '../constants/constants';
import { calculateAccuracy, getNonFlaggedErrors } from '../components/Reader/ReaderLibrary.js';
import * as Sentry from '@sentry/browser';
import {prefetchWordMetadata} from "./WordMetadataService.js";
import {setStudentGradeLevelWithAREA} from "./readerCharacteristics.js";
import { statusType, getActivityStatus } from "./displayStatus";
import {getProgressMonitoringStory} from "./progressMonitoring.js";
import {formatGuidForSIS,isNotNullOrUndefined,logSentryException,removeEmphasisTags,removeOpeningBreakTag,removeOpeningEllipsis,removeProsodyTags,sortActivitiesByCreatedDate} from './util';
import {isiPad} from './environment';
import device from './device/device';
import { logSessionInfo, storeUtteranceLog } from './logger';
import {filterByDate, filterByLocale, getSchoolYear} from '../components/Reports/util/validation';
import {inBenchmarkScreeningWindow, inCurrentScreeningWindow, getScreeningWindow} from './screeningWindowService';
import { uuidToHEX } from './util';
import { getNextEarlyReaderStory, isEarlyReaderInSequence } from 'Services/EarlyReaderService';
import { getAudioForPhonemes } from './phonemeToAudio';
import {promiseTimeout} from './util.js';
import {ASSIGNMENT_TYPE} from "Services/assignment/constants";
import { getSaxonSequenceStart } from './saxonService';
import { getPhrasesPerPage } from './APIUtils';
import { fixActivitiesParenting } from './APIFixParentChildRelationship.js';
import get from 'lodash.get';
import { isScorableEarlyReaderStory } from './EarlyReaderService';
import getArmScore from '../components/Reports/util/getArmScore';
import { GetStoryBranding, SetBranding } from './BrandingService';
import { redirectToLoginOrigin } from './loginService.js';
import { TTS_ENGINE_TYPE, createRandomSpeechMarks } from './generativeVoiceService.js';
import i18n from '../i18n/i18n.js';

export const DEFAULT_LOCALE = "en-us";

const DEFAULT_USER_STORE_DATA = {
  userId: null,
  sessionId: null,
  userType: null,
  userAgent: null,
  sessionStartTime: null,
  sessionData: {},
};

var stories_store = [];
var utterance_store = {};
var userData_store = {...DEFAULT_USER_STORE_DATA};

const defaultSORConfig = {
  enableERPictures: true,
  enablePracticePictures: true,
  enableGiveWord: true,
  enableFandP: false,
  enableDRA: false,
  enableLexile: false,
  enableExtendedColumns: false,
  selectedStandard: "COMMON_CORE",
  interventionFrequency: "STANDARD",
  customARMLevelMapping: null,
};

// Set this to true to get a dump of all the word metadata in the console when you hit "read a story"
// NOTE: we should never deploy it this way - it will take a lot of time on the "choose a story" screen.
export const RUN_METADATA_DUMP = false;

if(RUN_METADATA_DUMP){
  // Story dump - remember to add chapter to the graphQL
  studentRecordService.getRecommendedStories("",3,'tutor',100,"")
  .then((stories)=>{
    stories.map((story)=>{
      let content = story.chapters[0].phrases.reduce((total,phrase)=>{
        return total += ' ' + phrase;
      },"");
    });
  })
  .catch(err=>{console.log("ERROR IN RECOMMEND:");throw new Error(err);});
}

//Set this to true in order to load the stories from sampleStories.js
const GET_STORIES_LOCALLY = false;

//Set this to true in order to load a hard coded assignment for all students, entering forced assessment mode
const FORCE_ASSESSMENT = process.env.FORCE_ASSESSMENT;

//Delete userData_store
export function clearUserData(){
  console.log("trying to sign out");
  userData_store = {...DEFAULT_USER_STORE_DATA};
  Auth.signOut()
  .catch(err => console.error("ERROR SIGNING OUT",err));
}

//return the userData_store
export function getUserData(){
  return userData_store;
}

export function setUserData(key, value){
  userData_store[key] = value;
}

export function setSessionActivityId(inActivityId, assessmentMode){
  userData_store.activityId = inActivityId;

  //Initialize the inference service session
  if(!assessmentMode && process.env.INFERENCE_SERVICE_URL && process.env.INFERENCE_SERVICE_EAST_2){
    const baseURL = getUserData().region && getUserData().region === 'us-east-2' ? process.env.INFERENCE_SERVICE_EAST_2 : process.env.INFERENCE_SERVICE_URL;
    const url =  baseURL + "/init/" + inActivityId;
    const requestBody = {
      isESL: getUserData().isESL ?? false,
    }
    if (getUserData().interventionSelectionWCPMScore) {
      requestBody.prev_equatedwcpm = getUserData().interventionSelectionWCPMScore;
    }
    return fetch(url,{
      method: "POST",
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody),
      mode: 'no-cors'
    })
    .catch((err)=>console.error("Error priming inference for activity",inActivityId, err));
  }
}

export const defaultConfig = {
  NWF_CONFIG: [false,true,true,false,false,false,false],
  LISTENING_COMP_CONFIG: [false,false,false,false,false,false,false],
  READING_COMP_CONFIG: [false,false,false,false,true,true,true],
  SPELLING_CONFIG: [false,false,false,false,false,false,false],
  LETTERNAME_CONFIG: [false,false,false,false,false,false,false],
  VOCABULARY_SCREENER_CONFIG: [false,false,false,false,false,false,false],
  LETTER_NAMING_AND_SOUNDS_CONFIG: [false,false,false,false,false,false,false],
};

async function getAppSettings(){
  return await studentRecordService.getEntitlements(9999999,9999999);
}

export function setV2InterventionUserParams(activities) {
  let interventionSelectionWCPMScore;
  let interventionSelectionARMScore;

  if (activities) {
    for (let i = 0; i < activities.length; i++) {
      if (!interventionSelectionWCPMScore && activities[i].scores) {
        interventionSelectionWCPMScore = activities[i].scores.equatedWCPM;
      }

      if (!interventionSelectionARMScore && activities[i].scoreEstimates && activities[i].scoreEstimates.ARM_estimate) {
        interventionSelectionARMScore = activities[i].scoreEstimates.ARM_estimate;
      }

      if (interventionSelectionWCPMScore && interventionSelectionARMScore){
        break;
      }
    }
  }

  if (interventionSelectionWCPMScore) {
    userData_store.interventionSelectionWCPMScore = interventionSelectionWCPMScore;
  }

  if (interventionSelectionARMScore) {
    userData_store.interventionSelectionARMScore = interventionSelectionARMScore;
  }
}

// save the user data
export async function saveUserData(authData,retries=3){
  // if authData doesn't have a userType, or the userType is invalid, we will default to STUDENT,
  // on the other hand, if the userType does exist, then we assign the entire authData to userData_store.
  // for now all routes are exposed for user type STUDENT
  let iPad = isiPad();

  return Auth.currentUserInfo()
  .then(async resp => {
    userData_store = {
      userId: resp.attributes['custom:sisId'] || resp.attributes.sub,
      sessionId: getSessionID(),
      userType: resp.attributes['custom:userType'] || userTypes.STUDENT,
      userAgent: navigator.userAgent,
      sessionStartTime: Date.now(),
      firstName: resp.attributes['custom:firstName'], //TODO: pull name from SIS
      lastName: resp.attributes['custom:lastName'],
      email: resp.attributes.email,
      userName: resp.username,
      browser: device.getBrowserForSession(),
      sessionData: {
        cognitoUserSession: authData.signInUserSession,
        authData: authData,
        isiPad: iPad,
        useIpadFallbacks: iPad && parseFloat(device.browser.version) < 13,
      },
      assessment: resp.attributes['custom:assessment'],
      skipMagic: resp.attributes.skipMagic,
      correlationid: resp.attributes.correlationid,
    };

    //Special case for side-loaded admins
    if(!resp.attributes['custom:sisId']){
      userData_store.skipMagic = true;
      userData_store.sideloadedUser = true;
    }

    let connection = {}
    if(navigator.connection){
      connection = {
        downlink: navigator.connection.downlink,
        effectiveType: navigator.connection.effectiveType,
        rtt: navigator.connection.rtt,
        saveData: navigator.connection.saveData
      }
    }

    userData_store.connection = connection;
    console.log(JSON.stringify(getUserData().connection));

    Sentry.configureScope((scope) => {
      scope.setUser(userData_store);
      scope.setTag("user-id", userData_store.userId);
      scope.setTag("user-type", userData_store.userType);
      scope.setTag("user-school", userData_store.schoolId);
      scope.setTag("user-district", userData_store.districtId);
    });
    console.log(process.env.gitHash);
    logSessionInfo("app_version",{gitHash: COMMITHASH});

    //NUCLEAR OPTION: Redirect the user to the site maintenance page
    userData_store.appSettings = await getAppSettings();
    if(userData_store.appSettings && userData_store.appSettings.siteUnderMaintenance){
      underMaintenance();
    }


    if(userData_store.userType === userTypes.STUDENT){
      userData_store.locale = DEFAULT_LOCALE;
      userData_store.screenerOverrides = {};

      //Get the URL for the speech
      const regionInfo = await getRegionalURL(authData.region);
      if(regionInfo){
        userData_store.speechStreamURL = regionInfo.url;
        userData_store.region =  regionInfo.region;
      }

      //Get student metatata
      let studentInfo = await studentInformationService.getStudent(userData_store.userId);
      if(studentInfo && studentInfo.metadata){
        userData_store.sisGrade = studentInfo.metadata.actual_grade;
        userData_store.isESL = studentInfo.metadata.esl;
        userData_store.hasSeenFTUE = studentInfo.metadata.hasSeenFTUE;
        userData_store.hasSeenDialogFTUE = studentInfo.metadata.hasSeenDialogFTUE;
        userData_store.ftueStatuses = studentInfo.metadata.ftueStatuses;
        userData_store.tutorInSpanish = studentInfo.metadata.tutorInSpanish;
        userData_store.assessmentLanguageOverride = studentInfo.metadata.assessmentLanguageOverride;
      }else{
        userData_store.isESL = false;
        userData_store.hasSeenFTUE = true;
        userData_store.tutorInSpanish = false;
      }

      if(userData_store.isESL){
        userData_store.assessmentPageNavEnabled = true; //We always want the next button with ESL kids
      }

      if(userData_store.tutorInSpanish){
        userData_store.locale = userData_store.tutorInSpanish ? 'es-mx' : DEFAULT_LOCALE;
        userData_store.bilingualLocale = 'es-mx';
        userData_store.bilingualMode = true;
      }

      const equated = true;
      return studentRecordService.studentActivities(getFormatedId(userData_store.userId),1,["assessment","tutor"], ["scored","rescored"], equated)
      .then(async (activities)=>{
        return (resp.attributes.skipMagic ? Promise.resolve({grade: resp.attributes.grade, districtId: resp.attributes.districtId, schoolId: resp.attributes.schoolId}) : getStudentInfo(resp.username))
          .then(async info =>{
            userData_store.actualGrade = info.grade > 6 ? 6 : info.grade;

            let assignedEarlyReader = studentInfo.metadata.early_reader;
            let assignedSpanishEarlyReader = studentInfo.metadata.spanish_early_reader;

            // For grade K and 1, default to ERSS if user has no previous activities
            if (activities.length === 0 && (userData_store.actualGrade === 0 || userData_store.actualGrade === 1)) {
              assignedEarlyReader = true;
              if (userData_store.isESL) {
                assignedSpanishEarlyReader = true;
              }
            }

            //early reader status
            userData_store.sequenceStartStories = await getNextEarlyReaderStory(userData_store.userId, assignedEarlyReader, assignedSpanishEarlyReader);
            let isEarlyReader = assignedEarlyReader && isEarlyReaderInSequence(userData_store.sequenceStartStories,'en-us');
            let isSpanishEarlyReader = assignedSpanishEarlyReader && isEarlyReaderInSequence(userData_store.sequenceStartStories,'es-mx');

            //persist early reader setting if it changed (i.e. if they finishedthe sequence)
            if(studentInfo.metadata.early_reader !== isEarlyReader){
              const metadata = {...studentInfo.metadata, early_reader: isEarlyReader};
              await studentInformationService.updateStudentMetadata(userData_store.userId, metadata);
              logSessionInfo("early_reader_change", studentInfo);
            }

            //persist spanish early reader setting if it changed (i.e. if they finishedthe sequence)
            if(studentInfo.metadata.spanish_early_reader !== isSpanishEarlyReader){
              const metadata = {...studentInfo.metadata, spanish_early_reader: isSpanishEarlyReader};
              await studentInformationService.updateStudentMetadata(userData_store.userId, metadata);
              logSessionInfo("early_reader_change", studentInfo);
            }

            userData_store.isEarlyReader = isEarlyReader;
            userData_store.isSpanishEarlyReader = isSpanishEarlyReader;

            setV2InterventionUserParams(activities);

            let lastActivity = null;
            if(activities.length > 0){

              lastActivity = activities[0];
              const scores = lastActivity.scores;
              if (scores) {
                if (scores.areaScore){
                  userData_store.grade = setStudentGradeLevelWithAREA(scores.areaScore);
                  userData_store.AREA = scores.areaScore;
                }

                if (scores.decodeScore || scores.decodeScore === 0) {
                  userData_store.decodeScore = scores.decodeScore;
                }

                if (scores.wcpmScore && scores.wcpmScore > 0) {
                  userData_store.WCPM = scores.wcpmScore;
                }
              }
            }

            userData_store.grade = userData_store.grade ? userData_store.grade : userData_store.actualGrade;

            if (userData_store.AREA === null || userData_store.AREA === undefined) {
              userData_store.AREA = getDefaultScores(userData_store.grade).areaScore;
            }

            if (userData_store.decodeScore === null || userData_store.decodeScore === undefined) {
              userData_store.decodeScore = getDefaultScores(userData_store.grade).decodeScore;
            }

            if (userData_store.WCPM === null || userData_store.WCPM === undefined) {
              userData_store.WCPM = getDefaultScores(userData_store.grade).wcpmScore;
            }

            //Check for Saxon sequence start
            let isSaxonReader = studentInfo.metadata.saxon;
            if(!isEarlyReader && isSaxonReader){
              userData_store.sequenceStartStories = await getSaxonSequenceStart(userData_store.userId,isSaxonReader,userData_store.actualGrade,userData_store.sequenceStartStories);
            }
            if(userData_store.sequenceStartStories.length){
              userData_store.startingEarlyReaderSequence = true;
            }
            userData_store.isSaxonReader = isSaxonReader;
            userData_store.districtId = info.districtId;
            userData_store.schoolId = info.schoolId;
            Sentry.setTag("school-id", userData_store.schoolId);
            Sentry.setTag("district-id", userData_store.districtId);
            if(info.districtId && info.schoolId){
              return studentRecordService.getEntitlements(info.districtId,info.schoolId)
              .then((resp)=>{
                //If there is no entitlement, default to true. This is pretty fundamental to not having a single point
                // of failure for users. It's better to give them more than to have everybody go down.
                if(resp){
                  // Set the branding here so its avaialable across the app for students
                  SetBranding(resp.branding);

                  userData_store.assessmentEnabled = ((resp.assessmentEnabled !== null) && (resp.assessmentEnabled !== undefined)) ? resp.assessmentEnabled : true;
                  userData_store.branding = resp.branding;
                  userData_store.partner = resp.partner;
                  userData_store.assessmentPageNavEnabled = resp.assessmentPageNavEnabled || true;
                  userData_store.tutorEnabled = resp.tutorEnabled;
                  userData_store.calibrationEnabled = resp.calibrationEnabled;
                  userData_store.demoMode = resp.demoMode;
                  userData_store.demoLockdown = resp.demoLockdown;
                  userData_store.dyslexiaEnabled = resp.dyslexiaEnabled;
                  userData_store.dyslexiaParentLetterEnabled = resp.dyslexiaParentLetterEnabled;
                  userData_store.parentReportEnabled = ((resp.parentReportEnabled !== null) && (resp.parentReportEnabled !== undefined)) ? resp.parentReportEnabled : true;
                  userData_store.nwfConfig = resp.nwfConfig || defaultConfig.NWF_CONFIG;
                  userData_store.listeningCompConfig = resp.listeningCompConfig || defaultConfig.LISTENING_COMP_CONFIG;
                  userData_store.readingCompConfig = resp.readingCompConfig || defaultConfig.READING_COMP_CONFIG;
                  userData_store.spellingConfig = resp.spellingConfig || defaultConfig.SPELLING_CONFIG;
                  userData_store.letternameConfig = resp.letternameConfig || defaultConfig.LETTERNAME_CONFIG;
                  userData_store.vocabularyScreenerConfig = resp.vocabularyScreenerConfig || defaultConfig.VOCABULARY_SCREENER_CONFIG;
                  userData_store.letterNamingAndSoundsConfig = resp.letterNamingAndSoundsConfig || defaultConfig.LETTER_NAMING_AND_SOUNDS_CONFIG;
                  userData_store.dyslexiaTestEnabled = ((resp.dyslexiaTestEnabled !== null) && (resp.dyslexiaTestEnabled !== undefined)) ? resp.dyslexiaTestEnabled : true;
                  userData_store.screenerEnabled = ((resp.screenerEnabled !== null) && (resp.screenerEnabled !== undefined)) ? resp.screenerEnabled : true;
                  userData_store.autoAssign = resp.autoAssign || false;
                  userData_store.schoolYearStartDate = resp.schoolYearStartDate;
                  userData_store.screeningStartDate = resp.screeningStartDate;
                  userData_store.screeningEndDate = resp.screeningEndDate;
                  userData_store.tutorWhitelist = resp.tutorWhitelist;
                  userData_store.iesEnabled = resp.iesEnabled;
                  userData_store.districtAdminBadgingEnabled = resp.districtAdminBadgingEnabled;
                  userData_store.expandedEarlyReaderEnabled = resp.expandedEarlyReaderEnabled;
                  userData_store.sorConfig = resp.sorConfig || defaultSORConfig;
                  userData_store.enablePretest = ((resp.enablePretest !== null) && (resp.enablePretest !== undefined)) ? resp.enablePretest : true;
                  userData_store.voiceOverrides = resp.voiceOverrides || [];
                  userData_store.badgingEnabled = resp.badgingEnabled || false;
                  userData_store.showBadNetworkResponse = resp.showBadNetworkResponse || false;
                  userData_store.curriculum = resp.curriculum

                  let types = Object.keys(assignmentTypes);
                  let formOverrides = {}
                  types.map(type=>formOverrides[type] = []);
                  userData_store.formOverrides = resp.formOverrides ? resp.formOverrides : formOverrides;
                  userData_store.spanishConfig = resp.spanishConfig || "BOTH_ONE_SESSION";
                  userData_store.useStruggleMonitor = (userData_store.appSettings && userData_store.appSettings.useStruggleMonitor) || true;
                  userData_store.windowDisabledByGrade = resp.windowDisabledByGrade;
                }else{
                  userData_store.assessmentEnabled = true;
                  userData_store.assessmentPageNavEnabled = true;
                  userData_store.tutorEnabled = true;
                  userData_store.calibrationEnabled = false;
                  userData_store.demoMode = false;
                  userData_store.demoLockdown = false;
                  userData_store.dyslexiaEnabled = true;
                  userData_store.dyslexiaParentLetterEnabled = false;
                  userData_store.parentReportEnabled = true;
                  userData_store.nwfConfig = defaultConfig.NWF_CONFIG;
                  userData_store.listeningCompConfig = defaultConfig.LISTENING_COMP_CONFIG;
                  userData_store.readingCompConfig = defaultConfig.READING_COMP_CONFIG;
                  userData_store.spellingConfig = defaultConfig.SPELLING_CONFIG;
                  userData_store.letternameConfig = defaultConfig.LETTERNAME_CONFIG;
                  userData_store.vocabularyScreenerConfig = defaultConfig.VOCABULARY_SCREENER_CONFIG;
                  userData_store.letterNamingAndSoundsConfig = defaultConfig.LETTER_NAMING_AND_SOUNDS_CONFIG;
                  userData_store.dyslexiaTestEnabled = true;
                  userData_store.screenerEnabled = true;
                  userData_store.autoAssign = false;
                  userData_store.spanishConfig = "BOTH_ONE_SESSION";
                  userData_store.sorConfig = defaultSORConfig;
                  userData_store.enablePretest = true;
                  userData_store.voiceOverrides = [];
                  userData_store.windowDisabledByGrade = null;

                  let types = Object.keys(assignmentTypes);
                  let formOverrides = {}
                  types.map(type=>formOverrides[type] = []);
                  userData_store.formOverrides = formOverrides;
                  userData_store.useStruggleMonitor = true;
                }

                //If the student is not ESL the configuration is always ENGLISH_ONLY
                if(!userData_store.isESL){
                  userData_store.spanishConfig = "ENGLISH_ONLY"
                }

                //Once we have all the "real" information on the student, check to see if any overrides are necessary
                if(userData_store.demoMode  || (process.env.IGNORE_STUDENT_HISTORY === 'TRUE')){
                  userData_store.grade = userData_store.actualGrade;
                  let defaultScores = getDefaultScores(userData_store.grade);
                  userData_store.AREA = defaultScores.areaScore;
                  userData_store.decodeScore = defaultScores.decodeScore;
                }
                if(studentInfo){
                  userData_store.tutorEnabled = ((studentInfo.metadata.tutorEnabled !== undefined) && (studentInfo.metadata.tutorEnabled !== null)) ? studentInfo.metadata.tutorEnabled : userData_store.tutorEnabled;
                  userData_store.assessmentEnabled = ((studentInfo.metadata.assessmentEnabled !== undefined) && (studentInfo.metadata.assessmentEnabled !== null)) ? studentInfo.metadata.assessmentEnabled : userData_store.assessmentEnabled;
                  userData_store.screenerEnabled = ((studentInfo.metadata.screenerEnabled !== undefined) && (studentInfo.metadata.screenerEnabled !== null)) ? studentInfo.metadata.screenerEnabled : userData_store.screenerEnabled;
                  console.log(userData_store)
                }

                if (userData_store.appSettings) {
                  //Apply any additional app-level overrides here (you need to use strict equality here. It's possible that these could be null or undefined)
                  if(userData_store.appSettings.assessmentEnabled === false){
                    userData_store.assessmentEnabled = false;
                  }

                  if(userData_store.appSettings.screenerEnabled === false){
                    userData_store.screenerEnabled = false;
                  }
                }

                return Promise.resolve(userData_store);
              });
            }else{
              //If there's nothing in licensing, allow both
              userData_store.assessmentEnabled = true;
              userData_store.tutorEnabled = true;
              return Promise.resolve(userData_store);
            }
          });
      });
    }else{
      return studentInformationService.getSchoolsAndDistricts(resp,authData,resp.username,userData_store.userId,userData_store.userType,resp.attributes.skipMagic)
      .then((resp)=>{
        userData_store.permissions = resp.permissions;
        userData_store.reportingRole = resp.reportingRole;
        userData_store.schools = resp.schools;
        userData_store.district = resp.district;
        userData_store.districtId = resp.district.id;
        userData_store.subdistrictId = resp.subdistrictId;
        userData_store.schoolId = resp.schools[0].id;
        Sentry.setTag("school-id", userData_store.schoolId);
        Sentry.setTag("district-id", userData_store.districtId);
        return studentRecordService.getEntitlements(resp.district.id,resp.schools[0].id)
        .then((entitlements)=>{
          // Set the branding here so its avaialable across the app for non-students
          SetBranding(entitlements ? entitlements.branding : null);
          i18n.changeLanguage(entitlements.reportingLanguage || "en");
          userData_store.reportingLanguage = entitlements?.reportingLanguage || "en";
          userData_store.dyslexiaEnabled = entitlements ? entitlements.dyslexiaEnabled : true;
          userData_store.branding = entitlements ? entitlements.branding : null;
          userData_store.partner = entitlements ? entitlements.partner : "Amira";
          userData_store.dyslexiaParentLetterEnabled = entitlements ? entitlements.dyslexiaParentLetterEnabled : false;
          userData_store.parentReportEnabled = entitlements ? (entitlements.parentReportEnabled !== false) : true;
          userData_store.texasResources = entitlements ? entitlements.texasResources : false;
          userData_store.schoolYearStartDate = entitlements ? entitlements.schoolYearStartDate : null;
          userData_store.sorConfig = (entitlements && entitlements.sorConfig) ? entitlements.sorConfig : defaultSORConfig;
          userData_store.resourceProviders = (entitlements && entitlements.resourceProviders) ? entitlements.resourceProviders : [];
          userData_store.saxonEnabled = userData_store.resourceProviders.includes("Saxon");
          userData_store.statePercentileCuts = entitlements ? entitlements.statePercentileCuts : null;
          userData_store.prereaderAudioEnabled = entitlements ? entitlements.prereaderAudioEnabled : null;
          userData_store.useLatestARM = entitlements ? entitlements.useLatestARM : false;
          userData_store.demoLockdown = entitlements ? entitlements.demoLockdown : false;
          userData_store.calibrationEnabled = entitlements ? entitlements.calibrationEnabled : false;
          userData_store.demoMode = entitlements ? entitlements.demoMode : false;
          userData_store.iesEnabled = entitlements ? entitlements.iesEnabled : false;
          userData_store.districtAdminBadgingEnabled = entitlements ? entitlements.districtAdminBadgingEnabled : false;
          userData_store.expandedEarlyReaderEnabled = entitlements ? entitlements.expandedEarlyReaderEnabled : false;
          userData_store.badgingEnabled = entitlements ? entitlements.badgingEnabled : false;
          userData_store.windowDisabledByGrade = entitlements.windowDisabledByGrade;
          userData_store.showBadNetworkResponse = entitlements ? entitlements.showBadNetworkResponse : false;
          userData_store.curriculum = entitlements ? entitlements.curriculum : null;
          userData_store.fourCutlineCategoriesEnabled = entitlements?.fourCutlineCategoriesEnabled || false;
          userData_store.tutorAssignmentEnabled = entitlements?.tutorAssignmentEnabled || false;
          userData_store.allowLetterNamingAndSoundsRescoring = entitlements.allowLetterNamingAndSoundsRescoring !== undefined &&
            entitlements.allowLetterNamingAndSoundsRescoring !== null ?
            entitlements.allowLetterNamingAndSoundsRescoring : false;
          // Make sure HMH has the right demo settings
          if(entitlements && userData_store.userName === 'hmh.demo@parkside.com'){
            userData_store.curriculum = null;
            userData_store.partner = "HMH";
            userData_store.resourceProviders = ["IntoReading"]
            userData_store.sorConfig.selectedStandard = "COMMON_CORE";
          }
          return Promise.resolve(userData_store);
        });
      });
    }
  })
  .catch(err => {
    console.log("Error in auth",JSON.stringify(err));
    if(retries){
      console.log("Retrying auth",retries);
      return saveUserData(authData,retries-1);
    }else{
      return Promise.reject(err);
    }
  });
}

export function magicGradeToAmiraGrade(inMagicGrade){
  const gradeLookup = [
    {label: "Kindergarden", grade: 0},  //NOTE: Misspelling intentional - this is how magic returns it.
    {label: "Kindergarten", grade: 0},
    {label: "First grade", grade: 1},
    {label: "Second grade", grade: 2},
    {label: "Third grade", grade: 3},
    {label: "Fourth grade", grade: 4},
    {label: "Fifth grade", grade: 5},
    {label: "Sixth grade", grade: 6}
  ];

  let grade = gradeLookup.filter(lookup => {
    return lookup.label === inMagicGrade;
  });

  let returnGrade = grade.length ? grade[0].grade : 6;

  return returnGrade;
}

export function getStudentInfo(userName){
  const url = "https://api.getmagicbox.com/services//user/v1.0/"+userName+"?token=11b2d93f06ac11e9a2d10a2dfa68e30a";

  return fetch(url,{method: "GET"})
  .then((resp) => resp.json())
  .then(function(resp){
    let info = {
      districtId: resp.data.districtID,
      schoolId: resp.data.schoolId,
    };
    info.grade = magicGradeToAmiraGrade(resp.data.grades[0]);
    return Promise.resolve(info);
  })
  .catch((err) => {
      return Promise.reject(err);
  });
}

function getSessionID () {
  // add user data when
  return `Amira-Field-Trial:${Date.now()}`;
}

// returns a deepCopy of the given object
// this can help us guard against accidentally mutating the store
function deepCopy(obj){
  return JSON.parse(JSON.stringify(obj));
}

// return all possible stories - NOTE: This is ONLY used when GET_STORIES_LOCALLY is true
export function getStories(){
  if(RUN_METADATA_DUMP){
    STORIES.forEach(story => {
    if(story.type == assignmentTypes.TUTOR){
      story.chapters[0].phrases.map(phrase => prefetchWordMetadata(phrase));
    }
    });
  }
  if(GET_STORIES_LOCALLY){
    return Promise.resolve(STORIES);
  } else{
    return Promise.reject(new Error("Tried to get all stories in existance from the back end!"));
  }
}

// get a specific story by id
export function getStory(id){
  id  = formatGuidForSIS(id);
  if(!GET_STORIES_LOCALLY){
    // the locale param changes the content instruction language, NOT the content language itself.
    let bilingualLocale = getUserData().bilingualLocale; // leyendo makes it 'es-mx
    let studentLocale = getUserData().locale; // student's bilingual setting
    let instructionLocale = bilingualLocale || studentLocale // undefined or whatever student locale setting is
    return studentRecordService.getStory(id, instructionLocale).then((response) => {
      return Promise.resolve(addStoryAttributes(response));
    });
  }else{
    for(let i=0;i<STORIES.length;i++){
      if(STORIES[i].storyId === id || STORIES[i].storyid === id)
      {
        return Promise.resolve(STORIES[i]);
      }
    }
    //Turns out this only throws during development. Putting it in the proper error format anyway
    const notFoundError = new Error("Story Not Found",{storyId:id})
    return Promise.reject(notFoundError);
  }
}

function addStoryAttributes(story){
  //TODO: consider adding this on the backend instead
  story.locale = getUserData().locale;
  if(story.type == assignmentTypes.SPELLING){
    story.nonAudio = true;
  }

  if(story.tags && story.tags.length){
    story.brand = GetStoryBranding(story.tags);
  }

  return story;
}

//NOTE: This is ONLY used when GET_STORIES_LOCALLY is true
function getLocalAssessmentStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    let recommended_stories = [];
    for(let i=0;i<inStories.length;i++){
      if((inStories[i].grade == inGrade) && (inStories[i].type == assignmentTypes.ASSESSMENT)){
        recommended_stories.push({
          title: inStories[i].title,
          author: inStories[i].author,
          id: inStories[i].storyId,
        });
        return recommended_stories;
      }
    }
  }
  else{
    throw new Error("Tried to call get assessment stories from back end");
  }
}

function getLocalSpellingStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    let userData = getUserData();
    let locale = userData.locale;
    if(locale == "es-mx"){
      return [inStories[4]];
    } else {
      return [inStories[3]];
    }
  }else{
    throw new Error("Tried to call get local spelling stories instead of pulling from backend");
  }
}

function getLocalQuizStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    return [inStories[5]];
  }else{
    throw new Error("Tried to call get local spelling stories instead of pulling from backend");
  }
}

function getLocalNWFStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    let userData = getUserData();
    let locale = userData.locale;
    if(locale == "es-mx"){
      return [inStories[2]];
    } else {
      return [inStories[1]];
    }
  }else{
    throw new Error("Tried to call get local NWF stories instead of pulling from backend");
  }
}

function getLocalDyslexiaStories(inStories,inGrade){
  //NOTE: we don't currently have a local spanish version of the dyslexia
  if(GET_STORIES_LOCALLY){
    return [inStories[3]];
  }else{
    throw new Error("Tried to call get local dyslexia stories instead of pulling from backend");
  }
}

function getLocalReadAloudStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    // return [inStories[66]]; //audio
    return [inStories[0]]; //video
  }else{
    throw new Error("Tried to call get local read aloud stories instead of pulling from backend");
  }
}

//NOTE: This is ONLY used when GET_STORIES_LOCALLY is true
function getLocalTutorStories(inStories,inGrade,inNumberOfStories){
  if(GET_STORIES_LOCALLY){
    let recommended_stories = [];
    for(let i=0;i<inStories.length;i++){
      if((inStories[i].grade == inGrade) && (inStories[i].type == assignmentTypes.TUTOR)){
        recommended_stories.push({
          title: inStories[i].title,
          author: inStories[i].author,
          id: inStories[i].storyId,
          storyid: inStories[i].storyid,
        });
        if(recommended_stories.length > inNumberOfStories){
          break;
        }
      }
    }
    return recommended_stories;
  }else{
    throw new Error("Tried to call get tutor stories from back end");
  }
}

function getLocalReadingComprehensionStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    let readingCompStories = inStories.filter(s => s.type === assignmentTypes.READING_COMPREHENSION);
    return [readingCompStories[0]];
  }else{
    throw new Error("Tried to call get local reading comprehension stories instead of pulling from backend");
  }
}

function getLocalVocabularyScreenerStories(inStories,inGrade){
  if(GET_STORIES_LOCALLY){
    let vocabScreenerStories = inStories.filter(s => s.type === assignmentTypes.VOCABULARY_SCREENER);
    return [vocabScreenerStories[0]];
  }else{
    throw new Error("Tried to call get local vocabulary screener stories instead of pulling from backend");
  }
}

// get the recommended stories for a particular user
// right now this just returns the first three stories from our stories store
// eventually it will take a studentId
export function getRecommendedStories(grade=1,type=assignmentTypes.TUTOR,storyID="",retries=2,sequenceMode,assignmentType,backoff=250){
  if (retries == null || retries == undefined) {
    retries = 2;
  }
  if(!GET_STORIES_LOCALLY){
    let userData = getUserData();

    let numStories = (type !== assignmentTypes.TUTOR) ? 6 : 5;
    let user = userData.userId;
    if((type==assignmentTypes.DYSLEXIA) && (userData.actualGrade || userData.actualGrade == 0)){
      grade = userData.actualGrade; //Always screen dyslexia students at their actual grade
    }

    if(type === assignmentTypes.ASSESSMENT && assignmentType === ASSIGNMENT_TYPE.PROGRESS_MONITORING){
      return getProgressMonitoringStory(grade,userData.userId,userData.spanishConfig)
      .then((story)=>{
        return Promise.resolve(story);
      })
      .catch(err => {
        logSentryException(err, "Error on getProgressMonitoringStory");
        return Promise.reject(err);
      });
    }else{
      let formOverrides = getUserData().formOverrides;
      let screenerOverrides = getUserData().screenerOverrides;
      let overrideToUse = formOverrides[type] && formOverrides[type][grade] ? formOverrides[type][grade] : null;

      //If there is a screener override (mainly used with calibration), use that instead
      if(screenerOverrides && screenerOverrides[type]){
        overrideToUse = screenerOverrides[type];
      }

      if(overrideToUse && userData.locale !== 'es-mx')
      {
        return getStory(overrideToUse)
          .then(story=>{
            return Promise.resolve([story]);
          });
      }else{
        return studentRecordService.getRecommendedStories(user,grade,type,numStories,storyID,sequenceMode, userData.locale)
        .then((stories)=>{
          stories = stories.filter((story)=>{
            if(userData.locale !== "es-mx"){
              return (!story.tags || (story.tags && !story.tags.includes("ES-MX") && !story.tags.includes("es-mx")));
            }else{
              return(story.tags && (story.tags.includes("ES-MX") || story.tags.includes("es-mx")));
            }
          });

          return Promise.resolve(stories);
        })
        .catch(err=>{
          logSessionInfo('error_in_recommend', { error: err, retriesRemaining: retries });
          if(retries){
            console.log("ERROR IN RECOMMEND: RETRYING STORY RECOMMENDATION REQUEST", err);
            return new Promise(
              resolve => setTimeout(
                () => resolve(getRecommendedStories(grade,type,storyID,retries-1,sequenceMode, userData.locale, backoff+250)),
                backoff
            ));
          }else{
            console.log("ERROR IN RECOMMEND: STORY RECOMMENDATION RETRIES FAILED", err);
            return Promise.reject(err);
          }
        });
      }
    }
  }else{
    return getStories().then((stories) => {
      if(type == assignmentTypes.ASSESSMENT){
        return getLocalAssessmentStories(stories,grade);
      } else if(type == assignmentTypes.DYSLEXIA){
        return getLocalDyslexiaStories(stories,grade);
      } else if(type == assignmentTypes.NWF){
        return getLocalNWFStories(stories,grade);
      } else if(type == assignmentTypes.SPELLING){
        return getLocalSpellingStories(stories,grade); //TODO: use a switch instead here
      } else if(type == assignmentTypes.LISTENING_COMPREHENSION){
        return getLocalReadAloudStories(stories,grade);
      } else if(type == assignmentTypes.READING_COMPREHENSION){
        return getLocalReadingComprehensionStories(stories,grade);
      } else if(type == assignmentTypes.VOCABULARY_SCREENER){
        return getLocalVocabularyScreenerStories(stories,grade);
      } else {
        //Warning: If we switch back to getting stories from the student record service
        // before the recommendation service is implemented, need to guard against this
        // by checking GET_STORIES_LOCALLY.
        return getLocalTutorStories(stories,grade,3);
      }
    });
  }
}

const tutorWhitelist = [
  { 
    "storyid": "93405360-362f-11e9-bb3b-e7e2c0de6bee",
    "title": "The Life Cycle of Butterflies",
    "AREA_85": 8.6,
    "AREA_95": 10.2,
  },
  {
    "storyid": "d2669700-1ed8-11eb-83fe-57c4f9003880",
    "title": "Mustache Day",
    "AREA_85": 7.4,
    "AREA_95": 10.1,
  },
  {
    "title": "Animal Homes",
    "storyid": "73d63580-f9b0-11ea-8ba7-4f55b44592d0",
    "AREA_85": 6.8,
    "AREA_95": 8.7,
  },
  {
    "title": "FYI for kids: Bigger than an elephant",
    "storyid": "aa9a8ee0-f9c9-11ea-9b55-25ca75ea58d7",
    "AREA_85": 8.1,
    "AREA_95": 10.1,
  },
  {
    "title": "Bumblebees",
    "storyid": "f9ce3830-0974-11eb-8a99-238a1e6e21be",
    "AREA_85": 8,
    "AREA_95": 10.9,
  }
];

//TODO: this is a hook. Once we have access to stories by grade, fill this in
export async function getStoriesByGrade(grade,type=assignmentTypes.TUTOR,assignmentType,selectStories=false){
  let storyID = "";

  const demoStories = [
    "19dcc770-74db-11e9-97ec-692434f2ed7f",
    "c12e53c0-1e70-11e9-ae42-8b4b4946542e",
    "1ede33a0-e658-11e9-b032-a3eb44035fc3",
    "93405360-362f-11e9-bb3b-e7e2c0de6bee",
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 4th Grade Story
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 5th Grade Story
  ];

  const spanishDemoStories = [
    "0396de80-2abc-11ec-a861-0fbd689a9a10",
    "ab02f450-e809-11ec-a610-0ba5ccbff49d",
    "186718c0-8c1e-11ec-8b46-99c46be73253",
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 3rd Grade Story
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 4th Grade Story
    "93405360-362f-11e9-bb3b-e7e2c0de6bee", //###NEED 5th Grade Story
  ];

  //These are the default tutor stories that a student will get if they haven't been assessed
  const tutorOverride = [
    "266a5ee0-db3f-11eb-9f17-b5ec7c0477f0",
    "654eb920-db40-11eb-9f17-b5ec7c0477f0",
    "1b8e30c0-db42-11eb-9f17-b5ec7c0477f0",
    "007da520-436b-11ee-9310-b1a78efda0ba",
    "d43ddcd0-203d-11eb-83fe-57c4f9003880",
    "95754380-0125-11eb-a111-7f2b0f4514dc",
    "95754380-0125-11eb-a111-7f2b0f4514dc"
  ];

  const spanishTutorOverride = [
    "0396de80-2abc-11ec-a861-0fbd689a9a10",
    "10a6e750-1004-11ec-8b5b-a77cd59fd6b9",
    "33eda100-143b-11ec-a8ee-679889a2bda0",
    "e20be2c0-219b-11ec-b074-6727926623c1",
    "fdbc4230-1679-11ec-9a76-6727f7be53e0",
    "bb2d0060-2094-11ec-a3c7-33c4d99154fa",
    "bb2d0060-2094-11ec-a3c7-33c4d99154fa"
  ];

  if(grade > demoStories.length) grade = demoStories.length;
  let sequenceStart = getUserData().sequenceStartStories.find(element => element.locale === getUserData().locale);

  if((type == assignmentTypes.TUTOR) && (selectStories || getUserData().demoMode)){
    let demoTable = getUserData().locale === 'es-mx' ? spanishDemoStories : demoStories;
    storyID = demoTable[grade];
    return getStory(storyID)
    .then(story=>{
      return Promise.resolve([story]);
    });
  }else{
    if((type == assignmentTypes.TUTOR) && (getUserData().forceTutor) && !GET_STORIES_LOCALLY && !sequenceStart){
      storyID = getUserData().locale == 'en-us' ? tutorOverride[grade] : spanishTutorOverride[grade];
      userData_store.forceTutor = false; //Make sure if they complete the story they can move on.
      return getStory(storyID)
      .then(story=>{
        return Promise.resolve([story]);
      });
    }else if(type == assignmentTypes.TUTOR && (getUserData().isEarlyReader || getUserData().isSpanishEarlyReader || getUserData().isSaxonReader)){
      const hasSequenceStart = getUserData().sequenceStartStories.filter((story)=>story.locale === getUserData().locale).length > 0;
      if(getUserData().startingEarlyReaderSequence && hasSequenceStart){
        if(sequenceStart){
          storyID = sequenceStart.storyId;
          // This makes sure that the user doesn't continually get forced into the first story if they keep taking stories in this session
          let dropSequence = getUserData().sequenceStartStories.filter((story)=>story.storyId !==sequenceStart.storyId);
          setUserData("sequenceStartStories",dropSequence);
        }
      }else{
        // If Saxon or Spanish, just use the old method of getting the next story in the sequence. If English, override with the next story
        if(getUserData().locale === 'en-us' && !getUserData().isSaxonReader && getUserData().isEarlyReader){
          const nextStory = await getNextEarlyReaderStory(getUserData().userId,true);
          storyID = nextStory[0] ? nextStory[0].storyId : null;
        }
      }
      userData_store.forceTutor = false; //Make sure that if this is a new user then they don't get forced into the opening story in this session
      return await getRecommendedStories(grade,type,storyID,null,recommendationModes.SEQUENCE_LOCKED);
    }else{
      if(type == assignmentTypes.DYSLEXIA){
        return getDyslexiaStories(type,grade);
      }else if(type === assignmentTypes.READING_COMPREHENSION){
        const readingCompStories = await getRecommendedStories(grade,type);
        const currentScreeningWindow = getScreeningWindow();
        return readingCompStories;
        //###Temporarily disable this: return [readingCompStories.find(s => s.tags && s.tags.length && s.tags.includes(currentScreeningWindow))];
      }else {
        if(type == assignmentTypes.TUTOR && getUserData().tutorWhitelist){
          return recommendFromWhitelist();
        }else{
          return getRecommendedStories(grade,type,storyID,null,null,assignmentType);
        }
      }
    }
  }
}

async function recommendFromWhitelist(whiteList = tutorWhitelist){
  let takenActivities = await studentRecordService.checkForCompletedActivities(getUserData().userId);

  let availableStories = whiteList.filter((story)=>{
    return (takenActivities.filter((activity)=>{
      return ((activity.storyId === uuidToHEX(story.storyid)) && (activity.status == "scored" || activity.status == "rescored" || activity.displayStatus == "completed"))
    }).length === 0);
  });

  if(availableStories.length){
    //Recommend the story that has the closest to the midpoint between AREA_85 and AREA_95
    let difficulties = availableStories.map((story)=>{
      let diff = (story.AREA_95 - story.AREA_85)/2 + story.AREA_85;
      return Math.abs(getUserData().AREA - diff);
    })
    let indexOfSmallest = difficulties.indexOf(Math.min.apply(Math, difficulties));
    console.log("RECOMMEND",getUserData().AREA,availableStories[indexOfSmallest]);
    return studentRecordService.getStory(availableStories[indexOfSmallest].storyid)
    .then((story)=>{return Promise.resolve([story])});
  }else{
    return [];
  }

}

function getDyslexiaStories(type,grade){
  //NOTE: We don't have alternate letter name forms. Leave this in as a placeholder until and unless we re-enable this.
  const dyslexiaOverride = [
    null,
    null,
    null,
    null,
    null,
    null,
    null
  ];

  let formOverrides = getUserData().formOverrides;
  let locale = getUserData().locale;

  //If this is a calibration assignment, override everything (including formOverrides) with that;
  const {screenerOverrides} = getUserData();
  let calibrationOverride = screenerOverrides && screenerOverrides.dyslexia;
  let formToUse = calibrationOverride ? calibrationOverride : formOverrides[assignmentTypes.DYSLEXIA] && formOverrides[assignmentTypes.DYSLEXIA][grade];

  //No form override for spanish (yet)
  if(!(locale === "es-mx") && formToUse){
    return getStory(formToUse)
      .then(story=>{
        return Promise.resolve([story]);
      });
  }else{
    //If the letter names are configured and we have a letter name form, grab it. Otherwise just grab the "normal" test.
    if(!(locale === "es-mx") && (type == assignmentTypes.DYSLEXIA) && dyslexiaOverride[grade] && getUserData().letternameConfig[grade]){
      return getStory(dyslexiaOverride[grade])
      .then(story=>{
        return Promise.resolve([story]);
      })
    }else{
      return getRecommendedStories(grade,type);
    }
  }
}

// gets a story by id and then paginates it for use in the tutor app
export function getPaginatedStory(id, phrasesPerPage){
  return getStory(id).then((story) => {
    return paginateStory(story, getPhrasesPerPage(story) || phrasesPerPage);
  });
}

// gets a story by id and then squashes it for use in assessment
export function getSquashedStory(id){
  return getStory(id).then((story) => {
    return paginateStory(squashStory(story));
  });
}

// gets a story paginated at 1 phrase per page, combining phrases as necessary to make long enough pages for assessment mode
export function getAssessmentStory(id){
  return getStory(id).then((rawStory) => {
    let story = addStoryAttributes(rawStory);
    let wordsPerPhrase = 50;
    if(story.type != assignmentTypes.ASSESSMENT){ //TODO: get this from the assignment object
      wordsPerPhrase = null;
    }
    let paginatedStory = paginateStory(story, 1, wordsPerPhrase);
    return paginatedStory;
  });
}

// divides story into a given number (default 3) of phrase pages
export function paginateStory(story, phrasesPerPage=3, wordsPerPhrase=null){
  let pages = [];
  for(let i = 0; i < story.chapters.length; i++){
    let chapter = story.chapters[i];
    let currentPhrase = "";
    let currentPage = [];
    for(let j = 0; j < chapter.phrases.length; j++){
      let phrase = chapter.phrases[j];

      //Map logical item types to display types
      if(chapter.items && chapter.items[j]){
        chapter.items[j].type = getDisplayType(chapter.items[j].type);
        if(chapter.items[j].instructions && chapter.items[j].instructions.type){
          chapter.items[j].instructions.type = getDisplayType(chapter.items[j].instructions.type);
        }
      }

      if(!wordsPerPhrase || currentPhrase.split(" ").length + phrase.split(" ").length > wordsPerPhrase){
        if(currentPhrase !== ""){
          currentPage.push(currentPhrase);
        }
        currentPhrase = phrase;
      } else {
        currentPhrase = currentPhrase + " " + phrase;
      }

      // list of item types that should be one phrase per page
      const typesWithOnePhrasePerPage = [phraseTypes.NONSENSE_WORD_REPETITION,
        phraseTypes.CHANGE_ONE_PART,
        phraseTypes.WORD_PART_DELETION,
        phraseTypes.UP_AND_DOWN
      ];

      if((currentPhrase === "") && typesWithOnePhrasePerPage.includes(get(chapter, "items["+j+"].type"))){
        currentPage.push(currentPhrase);
        pages.push(currentPage);
        currentPhrase="";
        currentPage = [];
      }else{
        if(currentPage.length >= phrasesPerPage){
          pages.push(currentPage);
          currentPage = [];
        }

        // if this is the last phrase add the current phrase and page we are working on even if they are not long enough yet
        if(j >= chapter.phrases.length - 1){
          currentPage.push(currentPhrase);
          pages.push(currentPage);
        }
      }
    }
  }

  return {
    title: story.title,
    author: story.author,
    alternateIntroText: story.alternateIntroText,
    pages: pages,
    items: story.chapters[0].items,
    phrases: story.chapters[0].phrases,
    questions: story.questions,
    storyId: story.storyId,
    type: story.type,
    version: story.version,
    grade: story.grade,
    nonAudio: story.nonAudio,
    brand: story.brand,
    tags: story.tags,
    metadataOverrides: story.chapters[0].overrides,
    referenceStoryId: story.referenceStoryId,
    referenceStoryPhrases: story.referenceStoryPhrases,
  };
}

  //This method maps the abstract types of an item to our display types
function getDisplayType(type){
    switch(type){
      case 'blending':
        return "video";
      case 'wordList':
        return "word";
      case 'letterSound':
        return "sound";
      case 'ran':
        return "RAN";
      case 'spelling':
        return "textInput";
      case 'quiz':
        return 'quiz';
      case 'readAloudVideo':
        return 'readAloudVideo';
      default:
        return type;
    }
}

// returns a story with all phrases concatinated into one single phrase
function squashStory(story){
  let metaPhrase = "";
  for(let i = 0; i < story.chapters.length; i++){
    let chapter = story.chapters[i];
    for(let j = 0; j < chapter.phrases.length; j++){
      let phrase = chapter.phrases[j];
      if(!(i == 0 && j == 0)){
        metaPhrase += " ";
      }
      metaPhrase += phrase;
    }
  }
  return({
    title: story.title,
    author: story.author,
    storyId: story.storyId,
    version: story.version,
    type: story.type,
    storyTags: story.tags,
    chapters: [
      {phrases: [metaPhrase]}
    ],
  });
}


//TODO: this should not be necessary when we are getting back scored story data from the back end
// unwrap phrases with possibility of adding meta data. originally was considering keeping errors,
// mispronunciations etc in here, but since we are mutating them parallel arrays makes it easier for now
function unwrapPhrases(activity){
  let story = activity.story;
  let errors = activity.errors;
  let phrases = [];
  for(let i = 0; i < story.chapters.length; i++){
    let chapter = story.chapters[i];
    for(let j = 0; j < chapter.phrases.length; j++){
      let phrase = chapter.phrases[j];
      let phraseErrors = errors ? errors[j] : [];   //relying on the fact that we are not actually subdividing by chapter, and that the errors array is not currently indexed by chapter
      let markedPhrase = {
        text: phrase,
      }
      phrases.push(markedPhrase);
    }
  }
  return phrases;
}

//returns an object containing a paginated story, an audio file of a student reading it, and amira's guess at errors
// to be used in teacher correction UI
export function getScoredStory(activityId){
  return studentRecordService.getActivity(activityId).then((response) => {
    let activity = response.data.getActivity[0];
    //Handle empty error elements
    let phrases = activity.story.chapters[0].phrases;
    let errors = activity.errors;
    for(let i=0;i<errors.length;i++){
      if(errors[i].length === 0){
        errors[i] = new Array(phrases[i].split(' ').length).fill(true);
      }
    }

    //TODO: this really should not be three chained calls on this end, this info needs to be consolidated on the backend

    return ({
      ...activity,
      squashedStory: squashStory(activity.story),
      audio: null,                              //activity.audio,  TODO: link this on the backend
      mispronunciations: activity.mispronunciations || [],
      skips: activity.skips || [],
      selfCorrections: activity.selfCorrections || [],
      phrases: unwrapPhrases(activity),
      sidebarStatus: getActivityStatus(activity,false),
      updatedAt: new Date(activity.updatedAt),
      flaggedPhrases: activity.flagging,
      wcpmScore: activity.scores.wcpmScore,
    });
  }).then((scoredStory) => {
    // after we get back the story, populate the audio field manualy. Eventually this will be linked on the back end
    return searchSessionAudio(activityId).then((sessionAudio) => {
      scoredStory.audio = {url: sessionAudio};
      return scoredStory;
    });
  }).then((scoredStory) => {
    return studentInformationService.getUserInfoByUserId(scoredStory.studentId).then((studentData) => {
      if(!studentData){
        return scoredStory;
      }

      let student = {
        studentId: scoredStory.studentId,
        firstName: studentData.FirstName,
        lastName: studentData.LastName,
        userName: studentData.UserName,
      }
      scoredStory.student = student;
      return scoredStory;
    });
  });
}

export async function checkForAnyCompletedActivity(studentId){
  let returnValue = false;
  let completed = await studentRecordService.checkForCompletedActivities(studentId);

  completed.map((activity)=>{
    // We are going to consider a previous activity completed if it is tutor or assessment AND:
    // 1. It is scored or rescored OR
    // 2. It is a tutor and has been completed, but not necessarily scored OR
    // 3. The student has been classified as a prereader
    if(((activity.type === assignmentTypes.ASSESSMENT) || (activity.type === assignmentTypes.TUTOR))&&
      ((activity.status === statusType.SCORED) || (activity.status === statusType.RESCORED) ||
       ((activity.type === assignmentTypes.TUTOR) && (activity.displayStatus === statusType.COMPLETED)) ||
       (activity.displayStatus == statusType.PREREADER))){
        returnValue = true;
    }
  });

  return Promise.resolve(returnValue);
}

async function findAssignments(inActivities,studentId){
  let completedAssessment = false;
  let assignmentActivity = null;

  inActivities.map((activity)=>{
    //Check for an assignment, if we have one, we're essentially done.
    if(activity.status === activityStatus.ASSIGNED){
      if(!assignmentActivity){
        assignmentActivity = activity;
      }else{
        //Remove any duplicate assignments
        studentRecordService.markCompleted(activity.activityId,statusType.UNASSIGNED);
        logSessionInfo("unassigned_duplicate",activity);
      }
    }

    // We are going to consider a previous assessment completed if:
    // 1. It is scored or rescored (duh)
    // 2. It has been completed (this covers us with stuck or status of scoring)
    // 3. The student has been classified as a prereader
    if(activity.type === assignmentTypes.ASSESSMENT && inCurrentScreeningWindow(new Date(activity.createdAt)) &&
      ((activity.status === statusType.SCORED) || (activity.status === statusType.RESCORED) ||
       (activity.displayStatus === statusType.COMPLETED) || (activity.displayStatus == statusType.PREREADER))){
        if(activity.story.tags && activity.story.tags.includes("BENCHMARK")){
          completedAssessment = true;
        }
    }
  });

  //If there is neither a completed assignment nor an assigned activity, create one
  //TODO: remove benchmark window condition
  //TODO: continue support for autoAssign license?
  if(!completedAssessment && !assignmentActivity && (getUserData().autoAssign || inBenchmarkScreeningWindow())){
    return studentRecordService.createActivity("assessment", studentId, "1234", "assigned",null,"not_started",getUserData().districtId,getUserData().schoolId).then((response) => {
      if(response.data && response.data.putActivity && response.data.putActivity.activityId){
        return Promise.resolve(response.data.putActivity);
      }
    });
  }else{
    return Promise.resolve(assignmentActivity);
  }
}

// return average WCPM and accuracy and total points accrued for this student.
// If an activityId is passed in, we will make every attempt to get the current metrics from the backend
// by polling the activity for up to 5 seconds in case it hasn't scored yet. In the event that we don't get a score,
// the "current" versions of the metrics will be undefined.
export async function getStudentMetrics(currentActivityId=null, equated=false){
  return getValidActivitiesForStudents([{id: getUserData().userId}], 30, equated=equated).then(async activities => {
    let stats = {
      avgWPM: 0,
      avgAccuracy: 0,
      totalPointsAccrued: 0,
      testsTaken: 0,
      storiesRead: 0,
    };
    let logStatement = null;

    activities.forEach(activity => {
      stats.avgWPM += activity.scores.wcpmScore ? Math.round(activity.scores.wcpmScore) : 0;
       let thisAccuracy = activity.errors ? calculateAccuracy(getNonFlaggedErrors([activity.errors],activity.flagging)) : 0;
       stats.avgAccuracy += thisAccuracy;
       stats.totalPointsAccrued += activity.points || 0;
      stats.testsTaken += (activity.type === assignmentTypes.ASSESSMENT) ? 1 : 0;
      stats.storiesRead += (activity.type === assignmentTypes.TUTOR) ? 1 : 0;

      //If the activity passed in has already scored (which it will be if it's in the results of the call above)
      // then use it for the current stats
      if(activity.activityId === currentActivityId){
        stats.currentWCPM = Math.round(activity.scores.wcpmScore);
        stats.currentAccuracy = thisAccuracy;
        if (activity.scoreEstimates) {
          stats.currentARMScore = activity.scoreEstimates.ARM_estimate;
        }
        logStatement = 'metrics_activity_was_scored';
      }
    });

    //If the activity hasn't scored yet, poll for 5 seconds until it's scored
    if(!stats.currentWCPM && currentActivityId){
      let thisActivity = await studentRecordService.waitUntilScored(currentActivityId,5000);
      if(thisActivity){
        stats.currentWCPM = Math.round(equated ? thisActivity.scores.equatedWCPM : thisActivity.scores.wcpmScore);
        stats.currentAccuracy = calculateAccuracy(getNonFlaggedErrors([thisActivity.errors],thisActivity.flagging));
        if (thisActivity.scoreEstimates) {
          stats.currentARMScore = thisActivity.scoreEstimates.ARM_estimate;
        }
        logStatement = 'metrics_activity_eventually_scored';
      }
    }
    stats.avgWPM = activities.length ? Math.round(stats.avgWPM / activities.length) : 0;
    stats.avgAccuracy = activities.length ? Math.round(stats.avgAccuracy / activities.length) : 0;

    if(logStatement) logSessionInfo(logStatement,stats);

    return stats;
  }).catch(err => {
    console.log("error in getStudentMetrics: ", err);
    return {
      avgWPM: 0,
      avgAccuracy: 0,
      totalPointsAccrued: 0,
    };
  });
}

//stub to save teacher corrections to scoring
export function saveScoredAssessment(activityId, errors, skips, selfCorrections, mispronunciations, timeRead, flat = true){
  let formattedErrors = errors ? errors : [[]]; // TODO modify backend so that this is not necessary

  if(flat){
    formattedErrors = [errors.flat(4)];
  }

  //Remove any empty error elements (so we don't implicitly pad)
  formattedErrors = formattedErrors.filter(function (el) {
    return el.length > 0;
  });

  return studentRecordService.scoreActivity(activityId, formattedErrors, skips, selfCorrections, mispronunciations, timeRead);
}

export function getRhymingWord(inWord,callback)
{
  //Get a transcription
  let apiName = 'lexaSpeech';
  let path = '/RhymesWith';
  let myInit = { // OPTIONAL
    headers: {  'Content-Type': 'application/json'
    },
    body: {wordToRhyme: inWord},
    response: false // OPTIONAL (return entire response object instead of response.data)
  }

  //console.log('Sending...')
  API.post(apiName, path, myInit)
    .then(resp => {
        callback(resp);
    })
    .catch(err => {
      console.log('API call to Rhyme failed: ',err);
      return(err);
    })

}

function getUtteranceStoreKey(text, includeSpeechMarks, locale){
  return text.replace(/\s/g, '') + "_" + includeSpeechMarks + "_" + locale;
}

//can be called by itself to preload an utterance, just throw away the return value
export async function getUtterance(text, includeSpeechMarks=false, locale="en-us", timeout=10000, useGenerative=false){
  if(!text) return Promise.reject(new Error("no utterance provided",{text}));
  const key = getUtteranceStoreKey(text, includeSpeechMarks, locale);
  if(utterance_store.hasOwnProperty(key) && text){
    // if we've already stored this utterance, pull it from the cache
    // make sure to make it thenable
    return new Promise((resolve, reject) => {
      let returnValue = deepCopy(utterance_store[key]);
      returnValue.id = new Date().getTime();
      resolve(returnValue);
    }); //copy the utterance before returning to eliminate any chance of mutating the store
  } else {
    //Invoke the text to speech API
    if(!text.includes("<speak>")){
      text = '<speak>'+text+'</speak>';
    }
    let args = {
      textToSpeak: text,
      includeSpeechMarks: includeSpeechMarks,
      locale: locale,
      engine: useGenerative && locale.toLowerCase() === 'en-us' ? TTS_ENGINE_TYPE.generative : TTS_ENGINE_TYPE.standard,
    }

    //TODO: Think about this timeout and make sure we have retries.
    return promiseTimeout(getTextToSpeech(args),timeout)
    .then(async resp => {
      //Build the audio buffer
      let uInt8Array = new Uint8Array(resp.speech_audio.AudioStream.data);
      let arrayBuffer = uInt8Array.buffer;
      let blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
      let audioURL = URL.createObjectURL(blob);

      let utterance = {
        speech_marks: resp.speech_marks,
        speech_audio: resp.speech_audio.AudioStream.data,
        audioURL: audioURL,
        id: new Date().getTime(),
        text: resp.text
      };

      //cache the utterance
      utterance_store[key] = utterance;
      return Promise.resolve(deepCopy(utterance));
    })
    .catch(err => {return Promise.reject(err)});
  }
}

function handleVoiceOverride(locale){
  const override = getUserData().voiceOverrides;
  if(!override || override.length === 0){
    return locale;
  }else{
    const newLocale = override.find(o=>o.locale === locale);
    return newLocale ? newLocale.override : locale;
  }
}

export async function getTextToSpeech(args,retries=4) {
  let inText = args.textToSpeak;

  inText = inText.replace(/[“”]/g, '"');

  let returnSpeechMarks = args.includeSpeechMarks;
  let locale = handleVoiceOverride(args.locale);
  let engine = args.engine;

  if (engine === TTS_ENGINE_TYPE.generative) {
    // Generative voice does not support prosody tags, emphasis tags, opening break tags, or opening ellipsis
    inText = removeProsodyTags(inText);
    inText = removeEmphasisTags(inText);
    inText = removeOpeningBreakTag(inText);
    inText = removeOpeningEllipsis(inText);
  }

  //Get a transcription
  let apiName = 'lexaSpeech';
  let path = '/TextToSpeech';
  let myInit = { // OPTIONAL
    headers: {  'Content-Type': 'application/json'
    },
    body: {inText, locale, engine},
    returnSpeechMarks,
    locale: locale,
    response: false // OPTIONAL (return entire response object instead of response.data)
  };

  const utteranceLogParams = {
    text: inText,
    locale,
    includeSpeechMarks: returnSpeechMarks,
    engine
  }

  storeUtteranceLog(utteranceLogParams);
  console.log("TTS API:", utteranceLogParams);

  return API.post(apiName, path, myInit)
  .then(resp => {
    resp.text = inText;
    if (
      !resp.speech_marks ||
      resp.speech_marks.length === 0 ||
      !resp.speech_marks[0].words ||
      resp.speech_marks[0].words.length === 0
    ) {
      // Ensure we always have some sort of speech marks
      resp.speech_marks = createRandomSpeechMarks();
    }

    return Promise.resolve(resp);
  })
  .catch(err => {
    if(retries){
      console.log("RETRYING TTS")
      return getTextToSpeech(args,retries-1);
    }else{
      return Promise.reject(err);
    }
  });
}

function splitLongAttributes(inString,inBasePropertyName){
  let outObject = {};
  let index = 0;
  let propertyValue = "";
  do{
    propertyValue = inString.substring(index*1000,(index*1000)+1000);

    if(propertyValue){
      outObject[inBasePropertyName + (index ? index : "")] = propertyValue;
    }
    index++;
  }while(propertyValue && (index < 5));

  return outObject;
}

// TODO: this is a stub for a real api call. For now it is returning dummy data.
export function logQuiz(data){
  return Promise.resolve({
    ...data,
    status: "completed",
  });
}

export function saveAssessmentTiming(inActivityId,inTimings,inRaw,inRetries=3){
  const options = {
    bucket: process.env.TIMINGBUCKET,
    contentType: 'application/json',
    level: "public",
    customPrefix: {
      public: "",
    },
  };

  Storage.put(inActivityId+"_timing",JSON.stringify(inTimings),options)
  .then(()=>{
    if(inRaw) Storage.put(inActivityId+"_RAW",JSON.stringify(inRaw),options);
  })
  .catch((err) => {
    if(inRetries){
      console.log("RETRYING TIMING WRITE")
      logSessionInfo("retryTimingWrite",{error: err, options: options});
      saveAssessmentTiming(inActivityId,inTimings,inRaw,inRetries-1);
    }else{
      console.log("TIMING PUT FAIL:",err);
      logSessionInfo("timingWriteFail",{
        error: err,
        options: options,
        activityId: inActivityId,
        timings: inTimings,
        raw: inRaw
      });
    }
  });
}

export async function calculatePhraseSplits(timings,storyId,stopTime,shouldPad){
  return getStory(storyId)
  .then(story =>{
    let phraseIndex = 0;
    let phrases = story.chapters[0].phrases;
    let nextPhraseBreak = phrases[phraseIndex].split(' ').length;
    let slices = [{phrase: 0, start: 0}];
    let phraseBreaks = [nextPhraseBreak];
    //console.log("PHRASES:",phrases.length,JSON.stringify(phrases)); //Leave in for troubleshooting
    //console.log(JSON.stringify(timings)); //Leave in for troubleshooting
    for(let i=0;i<timings.length;i++){
      if(timings[i].position > nextPhraseBreak){
        if((i<timings.length - 1)&& shouldPad){
          slices[phraseIndex].end =  timings[i+1].time;
        }
        else{
          if(shouldPad || (i === 0)){
            slices[phraseIndex].end =  timings[i].time;
          }else{
            slices[phraseIndex].end =  timings[i-1].time;
          }

        }
        phraseIndex++;
        if(i>0){
          if(i>1 && shouldPad){
            slices.push({phrase: phraseIndex, start: timings[i-2].time});  //Enforce overlap
          }
          else{
            slices.push({phrase: phraseIndex, start: timings[i-1].time});  //Enforce overlap
          }
        }else{
          slices.push({phrase: phraseIndex, start: timings[i].time});  //Enforce overlap
        }
        if(phrases[phraseIndex]){
          nextPhraseBreak += phrases[phraseIndex].split(' ').length;
          phraseBreaks.push(nextPhraseBreak);
        }
      }
    }
    slices[phraseIndex].end = stopTime;
    //console.log("slices:",JSON.stringify(slices));  //Leave in for troubleshooting

    if(slices.length > phrases.length){
      slices = slices.slice(0,phrases.length);
    }
    //console.log("phrase breaks:",JSON.stringify(phraseBreaks)); //Leave in for troubleshooting
    return Promise.resolve(slices);
  })
  .catch(err => {console.log("ERROR CALCULATING TIMINGS:",err); return Promise.reject(err);});
}

//Using this helper function because the studentIds coming from SIS
//have a different format than the ones coming from the activities call
//TODO: We should store the studentId the same way in SIS and "ActivitiesDB" so we don't have to format these ids
function getFormatedId(id) {
  return String(id).replace(/\-/g, '').toUpperCase();
}

export function getParentReport(studentId, equated=false) {
  return studentRecordService.getParentReport(studentId, equated).then((data) => {
    return data;
  }, (error) => {
    console.log(error);
    throw error
  });
}

export function getAllAssessmentsForStudents(students, limit=null, type=["assessment"], equated=false, locale=null, startDate, endDate) {
  return getActivitiesWithStudentInfo(students, limit, type, ["assigned","scored","rescored","not_started","in_progress","scoring","underReview","reassess"], equated, locale, startDate, endDate);
}

export function getValidDyslexiaAssessmentsForStudents(students, cachedDyslexiaData, limit=null) {
  if(cachedDyslexiaData && cachedDyslexiaData.activities){
    return Promise.resolve(cachedDyslexiaData.activities)
  }
  const studentIds = students.map(({ id }) => getFormatedId(id));
  return studentRecordService.dyslexiaActivities(studentIds, limit)
    .then((activities) => {
      return activities
    });
}

export function filterDyslexiaAssessments(students, activities, window){
  // all dyslexia activities within the school year
  if(!getUserData().demoLockdown){
    activities = filterByDate(activities, window);
  }
  
  if(Array.isArray(activities) && activities.length > 0){
    let englishActivities = students.map(({id})=>{
      return activities.find(activity => ((getFormatedId(id) == activity.studentId) && !isSpanishActivity(activity)));
    });
    englishActivities = englishActivities.filter(act=> act);

    let spanishActivities = students.map(({id})=>{
      return activities.find(activity => ((getFormatedId(id) == activity.studentId) && isSpanishActivity(activity)));
    });
    spanishActivities = spanishActivities.filter(act=> act);

    let returnActivities = englishActivities.concat(spanishActivities);
    //return the activities with the student information
    return returnActivities.map((activity) => ({
      ...activity,
      student: students.filter(({ id }) => getFormatedId(id) == activity.studentId)[0],
    }));
  }
  return [];
}

export function isSpanishActivity(activity){
  return activity.tags && activity.tags.includes("ES_MX");
}

export function getValidActivitiesForStudents(students, limit=null, type=["assessment","tutor"], equated=false, locale=null, startDate, endDate) {
  if(students.length === 0){
    return Promise.resolve([]);
  }

  return getActivitiesWithStudentInfo(students, limit, type, ["scored","rescored"], equated, locale, startDate, endDate)
    .then((activities) => {
      console.log("getActivities",activities)
      return activities.filter((activity)=> activity && activity.scores && activity.scores.wcpmScore > 0 && activity.displayStatus !== statusType.UNASSIGNED);
    });
}

export function getParentActivitiesForStudents(students, limit=null, type=["assessment", "dyslexia", "letterNamingAndSounds"], equated=true, locale=null, startDate, endDate, statuses=null, displayStatuses=null, ) {
  if(students.length === 0){
    return Promise.resolve([]);
  }

  return getActivitiesWithStudentInfo(students, limit, type, statuses, equated, locale, startDate, endDate, displayStatuses)
    .then((activities) => {
      console.log("getActivities",activities)
      return activities.filter((activity)=> {
        // If the student is a prereader and has a dyslexia score, we want to show the dyslexia score
        if(activity?.displayStatus === 'prereader' &&
          activity?.children?.filter( childActivity => childActivity.type === 'dyslexia' &&
          isNotNullOrUndefined(childActivity.scores.armScore))?.length > 0){
          return true;
        }
        // Also want to handle the case where the student is a prereader and has a sibling with a dyslexia score (When taking )
        if(activity?.displayStatus === 'prereader' &&
          activity?.siblings?.filter( siblingActivity => siblingActivity.type === 'dyslexia' &&
          isNotNullOrUndefined(siblingActivity.scores.armScore))?.length > 0){
          return true;
        }
        return activity &&
          activity.scores &&
          activity.scores.wcpmScore > 0 &&
          activity.displayStatus !== statusType.UNASSIGNED
      });
    });
}

export function getProgressActivitiesForStudents(students, limit=null, type=["assessment","tutor","letterNamingAndSounds"], equated=false, locale, scale) {
  if(locale === locales.ES_MX) equated=false; //We don't support equating for spanish yet
  const studentIds = students.map(({ id }) => getFormatedId(id));
  const isARM = scale === "ARM";

  const currentSchoolYear = getSchoolYear();
  const { startDate, endDate } = currentSchoolYear

  return studentRecordService.progressActivities(studentIds, limit, type, equated, isARM, startDate, endDate)
    .then((activities) => {
      activities = filterByDate(activities);
      activities = filterByLocale(activities,locale);

      if(Array.isArray(activities) && activities.length > 0){
        //return the activities with the student information
        return activities.map((activity) => ({
          ...activity,
          student: students.filter(({ id }) => getFormatedId(id) == activity.studentId)[0],
        }));
      }
      return [];
    }).then((activities) => {
      return activities.filter((activity)=> {
        const armScore = getArmScore(activity.student.id, activity, locale);
        const isValidARM = armScore || armScore == 0 || activity.type === assignmentTypes.LETTER_NAMING_AND_SOUNDS;
        return activity && activity.scores && ((activity.scores.wcpmScore > 0) || isScorableEarlyReaderStory(activity.story) || isValidARM);
      });
    });
}

export function getActivitiesWithStudentInfo(students, limit=null, type=["assessment","tutor"], status=["scored","rescored","assigned","not_started","in_progress","scoring","reassess"], equated=false, locale, startDate, endDate, displayStatus=null){
  if(locale === locales.ES_MX) equated=false; //We don't support equating for spanish yet
  const studentIds = students.map(({ id }) => getFormatedId(id));
  const startDateUTC = startDate && new Date(startDate).toISOString();
  const endDateUTC = endDate && new Date(endDate).toISOString();
  return studentRecordService.studentActivities(studentIds, limit, type, status, equated, startDateUTC, endDateUTC, displayStatus)
    .then((activities) => {

      activities = filterByDate(activities);

      // fix the parent-child relationship issues
      if (getUserData().branding !== 'nwea-map-tutor'){
        activities = fixActivitiesParenting(activities);
      }


      if(locale){
        activities = filterByLocale(activities,locale);
      }
      //Take out assignment stubs
      activities = activities.filter((activity)=>activity.displayStatus !== statusType.UNASSIGNED);

      if(Array.isArray(activities) && activities.length > 0){
        //return the activities with the student information
        return activities.map((activity) => ({
          ...activity,
          student: students.filter(({ id }) => getFormatedId(id) == activity.studentId)[0],
        }));
      }
      return [];
    });
}

function getDefaultScores(inGrade){
  const default_scores = [
    {
        'areaScore': 5.200691551661794,
        'decodeScore': 79.56625405844156,
        'esriScore': 68.81435846200803,
        'phonAwareScore': 70.46186794464171,
        'vocabSize': 1911.5625,
        'wcpmScore': 3.89532300215477
    },
    {
        'areaScore': 6.303115044093312,
        'decodeScore': 85.62736687243763,
        'esriScore': 97.2541197320828,
        'phonAwareScore': 86.0491521913347,
        'vocabSize': 4495.773148148148,
        'wcpmScore': 27.52829808
    },
    {
        'areaScore': 7.003687317038951,
        'decodeScore': 90.82779859969637,
        'esriScore': 98.59781172056606,
        'phonAwareScore': 91.59326475798281,
        'vocabSize': 6012.501992031873,
        'wcpmScore': 66.19337371
    },
    {
        'areaScore': 7.805100135704936,
        'decodeScore': 92.61302800358887,
        'esriScore': 99.72856285704745,
        'phonAwareScore': 93.21768266287745,
        'vocabSize': 7125.880952380952,
        'wcpmScore': 86.08411187
    },
    {//###NEED Default scores for 4th
      'areaScore': 7.805100135704936,
      'decodeScore': 92.61302800358887,
      'esriScore': 99.72856285704745,
      'phonAwareScore': 93.21768266287745,
      'vocabSize': 7125.880952380952,
      'wcpmScore': 99.54759143
    },
    {//###NEED Default scores for 5th
      'areaScore': 7.805100135704936,
      'decodeScore': 92.61302800358887,
      'esriScore': 99.72856285704745,
      'phonAwareScore': 93.21768266287745,
      'vocabSize': 7125.880952380952,
      'wcpmScore': 115.1594649
    },
    {//###NEED Default scores for 6th
      'areaScore': 7.805100135704936,
      'decodeScore': 92.61302800358887,
      'esriScore': 99.72856285704745,
      'phonAwareScore': 93.21768266287745,
      'vocabSize': 7125.880952380952,
      'wcpmScore': 129.4522753
    }
  ];

  return ((inGrade || (inGrade == 0)) && inGrade < default_scores.length) ? default_scores[inGrade] : default_scores[default_scores.length-1];
}

export function endUserSession(){
  if(window.opener && !getUserData().sessionData.isiPad){
    window.open('', '_self').close();
  } else {
    if ((userData_store && userData_store.skipMagic) && !userData_store.sideloadedUser) {
      redirectToLoginOrigin(null, true);
    } else {
      location.reload();
    }
  }
}

export function underMaintenance(){
  window.location.href = "/underMaintenance.html";
}

let messageTimestamp = 0;
//https://stackoverflow.com/questions/28230845/communication-between-tabs-or-windows/28230846#28230846
// use local storage for messaging. Set message in local storage and clear it right away
// This is a safe way how to communicate with other tabs while not leaving any traces
//
export function message_broadcast(message)
{
  let messageToSend = {message: message};
  messageTimestamp = Date.now();
  messageToSend.timeStamp  = messageTimestamp;
  try{
    localStorage.setItem('amiraLogin',JSON.stringify(messageToSend));
  }catch(err){
    logSentryException(err, "Error message_broadcast");
    localStorage.removeItem('amiraLogin')
    try{
      localStorage.setItem('amiraLogin',JSON.stringify(messageToSend));
      console.log("could not shut down other windows");
    }catch(err2){
      logSentryException(err2, "Error message_broadcast2");
    }
  }
  localStorage.removeItem('amiraLogin');

}


// receive message
//
export function message_receive(ev)
{
    if (!ev || ev.key!='amiraLogin') return; // ignore other keys
    var message=JSON.parse(ev.newValue);

    if (!message || (ev.oldValue && JSON.parse(ev.oldValue).timeStamp == messageTimestamp)) return; // ignore empty msg or msg reset

    if((message.message == 'logout') && (message.timeStamp > messageTimestamp)){
      console.log("End session because of other tab",ev);
      if(getUserData()){
        logSessionInfo("amira_session_launched_in_other_tab",{key: ev.key, value: ev.newValue, time: Date.now()})
        .then(()=>{
          endUserSession();
        })
        .catch((err)=>{
          console.log("ERROR",err);
          endUserSession()
        });
      }else{
        console.log("NO LOG - not signed in")
      }
    }
}

export function findFilenames(phonemes, inLocale){
  return getAudioForPhonemes(phonemes, inLocale ? inLocale : getUserData().locale);
}